import React, { useState } from 'react';
import './FaqSection.css';

const FAQS = [
  {
    q: 'How is Oculo different from other AI extraction tools?',
    a: <>
      <p style={{ margin: 0 }}>NDE reports are dense, often messy documents where information is encoded in the spatial layout of the page. They arrive from multiple contractors, scanned in varying quality, using different terminology for the same data. Leading extraction tools can read the text, but they cannot reliably capture the relationships between fields. On dense NDE reports, they achieve 75-88% accuracy, making the output unusable without full manual review.</p>
      <p style={{ marginTop: 12 }}>Oculo was built for this. It extracts at 99.5%+ accuracy, captures field relationships, recognises when different contractors use different terms for the same data, and normalises everything into a consistent format across your project.</p>
      <p style={{ marginTop: 12, fontWeight: 700 }}>The vast majority of reports require no human intervention at all.</p>
      <p style={{ marginTop: 12 }}>Oculo also converts every document into a digital twin: an editable replica where every extracted value sits exactly where it appears on the original report. You review the document as you would normally read it, and confirm the extraction in seconds.</p>
    </>,
  },
  {
    q: 'What types of inspection reports can Oculo extract?',
    a: 'All inspection reports with a fixed layout, including integrity reports, welding reports, and any other structured NDE documentation. If your reports follow a consistent format, Oculo can extract them. For documents without a fixed layout, we offer additional extraction tools within the Orbix platform.',
  },
  {
    q: 'What is a digital twin of an inspection report?',
    a: 'An editable replica of the original report with all extracted data structured underneath. You can make changes directly in the document, and every change is tracked and versioned. Instead of comparing a list of captured values against the original, you verify the extraction by reading the document the way you normally would.',
  },
  {
    q: 'Is Oculo a digital reporting solution?',
    a: [
      'Oculo turns any inspection report into a fully digital, structured, editable record. Every report your teams and contractors already produce becomes a digital report the moment it enters the system.',
      'But it is not a digital reporting platform. There is no new system to adopt, no new format to enforce, no change to how your teams or contractors work. They keep producing reports exactly as they do today. Oculo makes those reports digital after the fact.',
    ],
  },
  {
    q: 'How accurate is Oculo?',
    a: '99.5%+ field-level accuracy on dense NDE reports. The vast majority of reports require no human correction at all. The system was built from the ground up to handle the variation we see in inspection reports: poor scan quality, skew, faded text, and the general messiness that comes with field documentation.',
  },
  {
    q: 'Is it complicated to set up?',
    a: 'No. All we need is one blank copy of the report. We create a template within minutes that can then be used to extract any number of documents that use that report format.',
  },
  {
    q: 'Can Oculo handle reports from different subcontractors?',
    a: 'Yes. Oculo is purpose-built to work with reports from different contractors, allowing you to go digital without changing the way your contractors do things. It also normalises terminology across reports, so when two contractors label the same data differently, your system receives consistent, structured output.',
  },
  {
    q: 'Can Oculo extract handwritten entries on inspection reports?',
    a: 'Yes. Oculo is capable of extracting handwritten entries with state-of-the-art accuracy. Accuracy for handwriting always depends on legibility, but the system is optimised to handle the hand-printed text commonly found on inspection forms.',
  },
  {
    q: 'Can extracted data be exported to our quality management or ERP system?',
    a: 'Yes. All data is extracted, normalised, and stored in a structured database. We offer API integration so that extracted inspection data can be automatically fed to your internal systems in the format you need.',
  },
  {
    q: 'How does Oculo fit into an existing inspection workflow?',
    a: 'Oculo is an application within the Orbix platform. Field teams from any number of projects or locations simply drag and drop their reports into their user portal. Extraction kicks off automatically, and the data is organised within the inspection management platform. It is designed to be simple enough for any technician to use without training.',
  },
  {
    q: 'How many reports can Oculo process at once?',
    a: 'There is no limit. The system is efficient and processes each report in seconds. Batch submissions of any size are handled without queuing delays.',
  },
  {
    q: 'Can AI really handle NDT documentation?',
    a: 'Most AI extraction tools were not designed for NDT. They work well on invoices, receipts, and general business documents, but they struggle with the density, terminology, and spatial complexity of inspection reporting. Oculo is different because it was engineered exclusively for NDT documentation — not a general AI tool adapted for inspections, but an AI extraction engine built from the ground up for the NDT industry.',
  },
];

function FaqSection() {
  const [openFaq, setOpenFaq] = useState(null);
  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <section style={{ position: 'relative', background: '#15102A', padding: '104px 56px', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', left: '50%', top: -80, transform: 'translateX(-50%)', width: 1120, height: 540, background: 'radial-gradient(ellipse at center, rgba(99,102,241,0.13), rgba(99,102,241,0) 66%)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', right: -160, bottom: -60, width: 560, height: 560, background: 'radial-gradient(ellipse at center, rgba(217,70,239,0.08), rgba(217,70,239,0) 64%)', pointerEvents: 'none' }} />
      <div style={{ position: 'relative', zIndex: 2, maxWidth: 1000, margin: '0 auto' }}>
        <div className="oc-reveal" style={{ textAlign: 'center', marginBottom: 50 }}>
          <span style={{ font: '700 12px Manrope,sans-serif', letterSpacing: '0.16em', color: '#816BFF' }}>FREQUENTLY ASKED QUESTIONS</span>
          <h2 style={{ margin: '14px 0 0', fontFamily: 'Montserrat,sans-serif', fontWeight: 800, fontSize: 40, lineHeight: 1.12, letterSpacing: '-0.02em', color: '#fff' }}>FAQ</h2>
        </div>
        <div className="oc-reveal" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {FAQS.map((f, i) => (
            <div key={i} className={'oc-faq-item' + (openFaq === i ? ' oc-open' : '')}>
              <button className="oc-faq-q" onClick={() => toggleFaq(i)}>
                <span>{f.q}</span>
                <i className="bx bx-plus oc-faq-icon" />
              </button>
              <div className="oc-faq-a">
                <div className="oc-faq-body">
                  {Array.isArray(f.a)
                    ? f.a.map((para, pi) => <p key={pi} style={{ margin: pi > 0 ? '12px 0 0' : 0 }}>{para}</p>)
                    : f.a}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default FaqSection;
